import { baseStringComparator } from '@instinet-analytics/core-lib';
import Column, { getBaseColumn } from './Column';
import ColumnType from './ColumnType';
import SortType from './SortType';

const baseStringColumns: Partial<Column<ColumnType.STRING>> = {
    ...getBaseColumn(ColumnType.STRING),
    sortCompareFunction: baseStringComparator,
    allowSetFilter: true,
};

/*
 * The symbol column does not have a dataField set here, as this will be set
 * depending on the Symbology app setting
 */
export const symbol = {
    ...baseStringColumns,
    name: 'Symbol',
    width: 57,
    tag: 'Symbol Data/Symbol',
    desc: 'Symbol name',
};

export const acct = {
    ...baseStringColumns,
    name: 'Account',
    dataField: 'acct',
    width: 63,
    tag: 'Order Data/Account',
    desc: 'Client account',
};

export const brokerName = {
    ...baseStringColumns,
    name: 'Broker',
    dataField: 'brokerName',
    width: 150,
    tag: 'Order Data/Broker',
    desc: 'Broker Name',
};

export const clientId = {
    ...baseStringColumns,
    name: 'Client ID',
    dataField: 'clientId',
    width: 68,
    tag: 'Order Data/Client ID',
    desc: 'Client ID',
    allowStrMatch: false,
};

export const clusterDescription = {
    ...baseStringColumns,
    name: 'Cluster Description',
    dataField: 'clusterDescription',
    width: 150,
    tag: 'Symbol Data/Cluster Description',
    desc: 'Cluster Description',
    allowStrMatch: true,
};

export const core = {
    ...baseStringColumns,
    name: 'Order Locale',
    dataField: 'core',
    width: 91,
    tag: 'Order Data/Order Locale',
    desc: 'Region from which the order originated',
    allowStrMatch: false,
};

export const country = {
    ...baseStringColumns,
    name: 'Country',
    dataField: 'country',
    width: 61,
    tag: 'Symbol Data/Country',
    desc: 'ISO Country Code',
    allowStrMatch: false,
};

export const curcy = {
    ...baseStringColumns,
    name: 'Currency',
    dataField: 'curcy',
    width: 65,
    tag: 'Symbol Data/Currency',
    desc: 'ISO Currency Code',
    allowStrMatch: false,
};

export const cusip = {
    ...baseStringColumns,
    name: 'Cusip',
    dataField: 'cusip',
    width: 55,
    tag: 'Symbology/Cusip',
    desc: 'CUSIP symbol',
    allowStrMatch: false,
};

export const idxEvtTrdCat = {
    ...baseStringColumns,
    name: 'Index Event',
    dataField: 'idxEvtTrdCat',
    width: 150,
    tag: 'Symbol Data/Index Event',
    desc: 'Index Event Trade Category',
    allowStrMatch: true,
};

export const ownCat = {
    ...baseStringColumns,
    name: 'Owner Category',
    dataField: 'ownCat',
    width: 105,
    tag: 'Order Data/Owner Category',
    desc: 'Owner category',
    allowStrMatch: true,
    editable: true,
};

export const paused = {
    ...baseStringColumns,
    name: 'Paused',
    dataField: 'paused',
    width: 68,
    tag: 'Order Data/Paused',
    desc: 'Order is paused',
    allowStrMatch: false,
    editable: false,
    caseInsensitive: false,
    sortable: true,
    defaultSortDesc: true,
};

export const reg = {
    ...baseStringColumns,
    name: 'Region',
    dataField: 'reg',
    width: 50,
    tag: 'Symbol Data/Region',
    desc: 'Region of the symbol primary exchange',
    allowStrMatch: false,
};

export const secType = {
    ...baseStringColumns,
    name: 'Security Type',
    dataField: 'secType',
    width: 150,
    tag: 'Symbol Data/Security Type',
    desc: 'Bloomberg security type',
    allowStrMatch: false,
};

export const sedol = {
    ...baseStringColumns,
    name: 'Sedol',
    dataField: 'sedol',
    width: 150,
    tag: 'Symbology/Sedol',
    desc: 'SEDOL symbol',
    allowStrMatch: false,
};

export const state = {
    ...baseStringColumns,
    name: 'Order Status',
    dataField: 'state',
    width: 74,
    tag: 'Order Data/Order Status',
    desc: 'Order status',
    allowStrMatch: false,
};

export const sym2 = {
    ...baseStringColumns,
    name: '2nd Symbol',
    dataField: 'sym2',
    width: 80,
    tag: 'Symbol Data/2nd Symbol',
    desc: 'Secondary symbol',
    allowStrMatch: true,
    editable: true,
};

export const parentId = {
    ...baseStringColumns,
    name: 'Parent ID',
    dataField: 'parentId',
    width: 200,
    tag: 'Order Data/Parent ID',
    desc: 'Parent ID',
    allowStrMatch: true,
    editable: false,
    caseInsensitive: false,
    sortType: SortType.BLANK,
};

export const basket = {
    ...baseStringColumns,
    name: 'Basket',
    dataField: 'basket',
    width: 150,
    tag: 'Order Data/Basket',
    desc: 'Order basket id',
};

export const bloom = {
    ...baseStringColumns,
    name: 'Bloomberg Sym',
    dataField: 'bloom',
    width: 102,
    tag: 'Symbology/Bloomberg Sym',
    desc: 'Bloomberg symbol',
};

export const client = {
    ...baseStringColumns,
    name: 'Client',
    dataField: 'client',
    width: 90,
    tag: 'Order Data/Client',
    desc: 'Client name',
};

export const clientAlias = {
    ...baseStringColumns,
    name: 'Client Alias',
    dataField: 'clientAlias',
    width: 90,
    tag: 'Order Data/Client Alias',
    desc: 'Client alias',
};

export const deliverToSub = {
    ...baseStringColumns,
    name: 'Deliver To',
    dataField: 'deliverToSub',
    width: 70,
    tag: 'Order Data/Deliver To',
    desc: 'Deliver To Sub ID',
};

export const desk = {
    ...baseStringColumns,
    name: 'Desk',
    dataField: 'desk',
    width: 90,
    tag: 'Order Data/Desk',
    desc: 'Trading desk',
};

export const deskId = {
    ...baseStringColumns,
    name: 'Desk ID',
    dataField: 'deskId',
    width: 35,
    tag: 'Order Data/Desk ID',
    desc: 'Trading desk ID',
};

export const ex = {
    ...baseStringColumns,
    name: 'Exch',
    dataField: 'ex',
    width: 60,
    tag: 'Symbol Data/Exch',
    desc: 'Symbol primary exchange',
};

export const handler = {
    ...baseStringColumns,
    name: 'Handler',
    dataField: 'handler',
    width: 90,
    tag: 'Order Data/Handler',
    desc: 'Order Handler',
};

export const ind = {
    ...baseStringColumns,
    name: 'Sector',
    dataField: 'ind',
    width: 120,
    tag: 'Symbol Data/Sector',
    desc: 'Symbol sector',
};

export const isin = {
    ...baseStringColumns,
    name: 'ISIN',
    dataField: 'isin',
    width: 55,
    tag: 'Symbology/ISIN',
    desc: 'ISIN symbol',
};

export const isymbol = {
    ...baseStringColumns,
    name: 'INET Symbol',
    dataField: 'isymbol',
    width: 91,
    tag: 'Symbology/Instinet Symbol',
    desc: 'Instinet Symbol name',
};

export const listId = {
    ...baseStringColumns,
    name: 'List ID',
    dataField: 'listId',
    width: 200,
    tag: 'Order Data/List ID',
    desc: 'Order List ID',
};

export const localSymC = {
    ...baseStringColumns,
    name: 'Local Sym',
    dataField: 'localSymC',
    width: 200,
    tag: 'Symbology/Local Sym Country',
    desc: 'Local symbol (symbol and country code)',
};

export const mic = {
    ...baseStringColumns,
    name: 'Mic',
    dataField: 'mic',
    width: 60,
    tag: 'Symbol Data/Mic',
    desc: 'Symbol primary exchange Market Identifier Code',
};

export const mktDataMic = {
    ...baseStringColumns,
    name: 'Market Mic',
    dataField: 'mktDataMic',
    width: 60,
    tag: 'Symbol Data/Market Mic',
    desc: 'Symbol market data Market Identifier Code',
};

export const name = {
    ...baseStringColumns,
    name: 'Name',
    dataField: 'name',
    width: 90,
    tag: 'Symbol Data/Name',
    desc: 'Symbol description',
};

export const orderInfo = {
    ...baseStringColumns,
    name: 'Order Info',
    dataField: 'orderInfo',
    width: 200,
    tag: 'Order Data/Order Info',
    desc: 'Order Information',
};

export const owner = {
    ...baseStringColumns,
    name: 'Owner',
    dataField: 'owner',
    width: 90,
    tag: 'Order Data/Owner',
    desc: 'Owner',
};

export const parentChild = {
    ...baseStringColumns,
    name: 'Parent/Child',
    dataField: 'parentChild',
    width: 120,
    tag: 'Order Data/Parent Or Child',
    desc: 'Parent or child order',
};

export const pairs = {
    ...baseStringColumns,
    name: 'Pairs ID',
    dataField: 'pairs',
    width: 200,
    tag: 'Order Data/Pairs ID',
    desc: 'null',
};

export const subInd = {
    ...baseStringColumns,
    name: 'Sub Industry',
    dataField: 'subInd',
    width: 150,
    tag: 'Symbol Data/Sub Industry',
    desc: 'Symbol sub-industry category',
};

export const sector = {
    ...baseStringColumns,
    name: 'Industry',
    dataField: 'sector',
    width: 150,
    tag: 'Symbol Data/Industry',
    desc: 'Symbol industry category',
};

export const stn = {
    ...baseStringColumns,
    name: 'Strategy',
    dataField: 'stn',
    width: 62,
    tag: 'Order Data/Strategy',
    desc: 'Strategy Type',
};

export const symbolIndex = {
    ...baseStringColumns,
    name: 'Symbol Index',
    dataField: 'symbolIndex',
    width: 90,
    tag: 'Symbol Data/Symbol Index',
    desc: 'Symbol Index',
};

export const t0 = {
    ...baseStringColumns,
    name: 'Tag 0',
    dataField: 't0',
    width: 90,
    tag: 'Order Data/Tag  0',
    desc: 'Tag 0',
};

export const t1 = {
    ...baseStringColumns,
    name: 'Tag 1',
    dataField: 't1',
    width: 90,
    tag: 'Order Data/Tag  1',
    desc: 'Tag 1',
};

export const t2 = {
    ...baseStringColumns,
    name: 'Tag 2',
    dataField: 't2',
    width: 90,
    tag: 'Order Data/Tag  2',
    desc: 'Tag 2',
};

export const t3 = {
    ...baseStringColumns,
    name: 'Tag 3',
    dataField: 't3',
    width: 90,
    tag: 'Order Data/Tag  3',
    desc: 'Tag 3',
};

export const t4 = {
    ...baseStringColumns,
    name: 'Tag 4',
    dataField: 't4',
    width: 90,
    tag: 'Order Data/Tag  4',
    desc: 'Tag 4',
};

export const t5 = {
    ...baseStringColumns,
    name: 'Tag 5',
    dataField: 't5',
    width: 90,
    tag: 'Order Data/Tag  5',
    desc: 'Tag 5',
};

export const t6 = {
    ...baseStringColumns,
    name: 'Tag 6',
    dataField: 't6',
    width: 90,
    tag: 'Order Data/Tag  6',
    desc: 'Tag 6',
};

export const t7 = {
    ...baseStringColumns,
    name: 'Tag 7',
    dataField: 't7',
    width: 90,
    tag: 'Order Data/Tag  7',
    desc: 'Tag 7',
};

export const t8 = {
    ...baseStringColumns,
    name: 'Tag 8',
    dataField: 't8',
    width: 90,
    tag: 'Order Data/Tag  8',
    desc: 'Tag 8',
};

export const t9 = {
    ...baseStringColumns,
    name: 'Tag 9',
    dataField: 't9',
    width: 90,
    tag: 'Order Data/Tag  9',
    desc: 'Tag 9',
};

export const t10 = {
    ...baseStringColumns,
    name: 'Tag 10',
    dataField: 't10',
    width: 90,
    tag: 'Order Data/Tag 10',
    desc: 'Tag 10',
};

export const t11 = {
    ...baseStringColumns,
    name: 'Tag 11',
    dataField: 't11',
    width: 90,
    tag: 'Order Data/Tag 11',
    desc: 'Tag 11',
};

export const t12 = {
    ...baseStringColumns,
    name: 'Tag 12',
    dataField: 't12',
    width: 90,
    tag: 'Order Data/Tag 12',
    desc: 'Tag 12',
};

export const t13 = {
    ...baseStringColumns,
    name: 'Tag 13',
    dataField: 't13',
    width: 90,
    tag: 'Order Data/Tag 13',
    desc: 'Tag 13',
};

export const t14 = {
    ...baseStringColumns,
    name: 'Tag 14',
    dataField: 't14',
    width: 90,
    tag: 'Order Data/Tag 14',
    desc: 'Tag 14',
};

export const t15 = {
    ...baseStringColumns,
    name: 'Tag 15',
    dataField: 't15',
    width: 90,
    tag: 'Order Data/Tag 15',
    desc: 'Tag 15',
};

export const t16 = {
    ...baseStringColumns,
    name: 'Tag 16',
    dataField: 't16',
    width: 90,
    tag: 'Order Data/Tag 16',
    desc: 'Tag 16',
};

export const t17 = {
    ...baseStringColumns,
    name: 'Tag 17',
    dataField: 't17',
    width: 90,
    tag: 'Order Data/Tag 17',
    desc: 'Tag 17',
};

export const t18 = {
    ...baseStringColumns,
    name: 'Tag 18',
    dataField: 't18',
    width: 90,
    tag: 'Order Data/Tag 18',
    desc: 'Tag 18',
};

export const t19 = {
    ...baseStringColumns,
    name: 'Tag 19',
    dataField: 't19',
    width: 90,
    tag: 'Order Data/Tag 19',
    desc: 'Tag 19',
};

export const t20 = {
    ...baseStringColumns,
    name: 'Tag 20',
    dataField: 't20',
    width: 90,
    tag: 'Order Data/Tag 20',
    desc: 'Tag 20',
};

export const t21 = {
    ...baseStringColumns,
    name: 'Tag 21',
    dataField: 't21',
    width: 90,
    tag: 'Order Data/Tag 21',
    desc: 'Tag 21',
};

export const t22 = {
    ...baseStringColumns,
    name: 'Tag 22',
    dataField: 't22',
    width: 90,
    tag: 'Order Data/Tag 22',
    desc: 'Tag 22',
};

export const t23 = {
    ...baseStringColumns,
    name: 'Tag 23',
    dataField: 't23',
    width: 90,
    tag: 'Order Data/Tag 23',
    desc: 'Tag 23',
};

export const t24 = {
    ...baseStringColumns,
    name: 'Tag 24',
    dataField: 't24',
    width: 90,
    tag: 'Order Data/Tag 24',
    desc: 'Tag 24',
};

export const wheelStrategy = {
    ...baseStringColumns,
    name: 'Trade Strategy',
    dataField: 'wheelStrategy',
    width: 90,
    tag: 'Order Data/Trade Strategy',
    desc: 'Trade (Wheel) Strategy',
};

export const appliedTradeStrategy = {
    ...baseStringColumns,
    name: 'Applied Trade Strategy',
    dataField: 'appliedTradeStrategy',
    width: 90,
    tag: 'Order Data/Applied Trade Strategy',
    desc: 'Applied Trade Strategy',
};
